import React, { useRef, useState, useMemo, useCallback } from "react";
import { LockOutlined, EditOutlined, DeleteOutlined, PushpinOutlined, PushpinFilled } from "@ant-design/icons";
import { Button, Modal, Form, List, Row, Col, Input, Tooltip, message } from "antd";
import { connect } from "dva";
import { dateStringToStandardFormat } from "@/utils/dateFormatter";
import { deleteNote, editNote, pinNote } from "@/services/notes";

const { TextArea } = Input;
const { confirm } = Modal;

const NoteItem = ({
  currentProfile,
  configPerm,
  item,
  concurrencyStamp,
  hideEditAndDeleteButtons = false,
  getNotes,
  onNotesCountChange,
  itemIndex,
}) => {
  const editNotesForm = useRef();

  const [hovered, setHovered] = useState(false);
  const [pinning, setPinning] = useState(false);
  const [editNotesVisible, setEditNotesVisible] = useState(false);
  const [loadingEditNotesSubmission, setLoadingEditNotesSubmission] = useState(false);

  const onPinNote = useCallback(() => {
    setPinning(true);

    pinNote(item.id)
      .then(() => {
        if (typeof getNotes === "function") {
          getNotes();
        }
      })
      .finally(() => {
        setPinning(false);
      });
  }, [item?.id, getNotes]);

  const cancelNotesEdit = useCallback(() => {
    editNotesForm.current.resetFields();
    setEditNotesVisible(false);
  }, []);

  const saveNotesEdit = useCallback(() => {
    editNotesForm.current.validateFields().then(res => {
      setLoadingEditNotesSubmission(true);

      editNote({ concurrencyStamp, id: item?.id, text: res.text }).then(() => {
        if (typeof getNotes === "function") {
          getNotes().then(() => {
            setEditNotesVisible(false);
            setLoadingEditNotesSubmission(false);
          });
        }
      });
    });
  }, [item?.id, concurrencyStamp, getNotes]);

  const onDeleteNote = useCallback(() => {
    confirm({
      title: "Are you sure?",
      content: "Are you sure you want to delete this note?",
      onOk: () => {
        const hide = message.loading("Deleting...");
        deleteNote(item.id).then(() => {
          if (typeof getNotes === "function") {
            getNotes().then(() => {
              hide();

              message.success("Note deleted successfully!");

              if (typeof onNotesCountChange === "function") {
                onNotesCountChange();
              }
            });
          }
        });
      },
    });
  }, [item?.id, getNotes, onNotesCountChange]);

  const buttons = useMemo(() => {
    const _buttons = [];
    const hasEditNotesPermission = configPerm["NoteService.Notes.Edit"];
    const hasDeleteNotesPermission = configPerm["NoteService.Notes.Delete"];
    const thisNoteIsMine = currentProfile.id === item.creatorId;

    if (!hovered && !pinning) {
      return _buttons;
    }

    if (hasEditNotesPermission) {
      if (item.pinned) {
        _buttons.push(
          <Tooltip title="Unpin note" key={`unpin-note-${item.id}`} color="white">
            <Button
              id={`note-item-unpin-button-${itemIndex}`}
              loading={pinning}
              icon={<PushpinFilled />}
              onClick={onPinNote}
            />
          </Tooltip>,
        );
      } else {
        _buttons.push(
          <Tooltip title="Pin note" key={`pin-note-${item.id}`} color="white">
            <Button
              id={`note-item-pin-button-${itemIndex}`}
              loading={pinning}
              icon={<PushpinOutlined />}
              onClick={onPinNote}
            />
          </Tooltip>,
        );
      }

      if (!item?.autoGenerated && !hideEditAndDeleteButtons && hasEditNotesPermission && thisNoteIsMine) {
        _buttons.push(
          <Tooltip title="Edit note" key={`edit-note-${item.id}`} color="white">
            <Button
              id={`note-item-edit-button-${itemIndex}`}
              type="primary"
              icon={<EditOutlined />}
              onClick={() => {
                setEditNotesVisible(true);
              }}
            />
          </Tooltip>,
        );
      }
    }

    if (!item?.autoGenerated && !hideEditAndDeleteButtons && hasDeleteNotesPermission && thisNoteIsMine) {
      _buttons.push(
        <Tooltip title="Delete note" key={`delete-note-${item.id}`} color="white">
          <Button
            id={`note-item-delete-button-${itemIndex}`}
            type="danger"
            icon={<DeleteOutlined />}
            onClick={onDeleteNote}
          />
        </Tooltip>,
      );
    }

    return _buttons;
  }, [
    currentProfile,
    configPerm,
    hovered,
    item,
    itemIndex,
    pinning,
    hideEditAndDeleteButtons,
    onPinNote,
    onDeleteNote,
  ]);

  return (
    <List.Item
      actions={buttons}
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
    >
      <List.Item.Meta
        title={item.text}
        description={
          <div>
            {dateStringToStandardFormat(item.creationTime, { showTime: true })}

            <span style={{ marginLeft: 12 }}>{item.userFullName}</span>
            {item.autoGenerated && (
              <Tooltip title="This is an auto generated note" color="white">
                <LockOutlined style={{ marginLeft: 12 }} />
              </Tooltip>
            )}
            {item.pinned && (
              <Tooltip title="This note is pinned" color="white">
                <PushpinFilled style={{ marginLeft: 12 }} />
              </Tooltip>
            )}
          </div>
        }
      />

      <Modal
        title="Edit Notes"
        visible={editNotesVisible}
        onOk={saveNotesEdit}
        onCancel={cancelNotesEdit}
        confirmLoading={loadingEditNotesSubmission}
      >
        <Form
          layout="vertical"
          ref={editNotesForm}
          initialValues={{
            text: item?.text,
          }}
        >
          <Row gutter={16}>
            <Col lg={24}>
              <Form.Item name="text" label="Note">
                <TextArea rows={4} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </List.Item>
  );
};

export default connect(({ user, configs }) => {
  return {
    currentProfile: user.currentProfile,
    configPerm: configs.configPerm,
  };
})(NoteItem);
